import {
  BillingFrequency,
  BillingInterval,
  LicensePlate,
  ParkZoneBenefit,
  Pmc,
} from 'types/generatedSchemaTypes';
import { create } from 'zustand';

export type VerificationStatus =
  | 'idle'
  | 'loading'
  | 'success'
  | 'pending'
  | 'failed';

export type SelectedPermitBillingInterval = {
  selectedBillingFrequency: BillingFrequency | undefined;
  billingIntervals: BillingInterval[];
};

export type UnsavedEditPermitAction = 'save' | 'discard' | null;
export type ConfirmDisableRenewalAction = 'confirm' | null;

interface HsaIdState {
  hsaIdCode: string;
  customerVerificationStatus: VerificationStatus;
}

interface PermitState {
  hsaIdState: HsaIdState;
  pmcIsLockedByMemberGroup: Record<
    string,
    {
      pmcIsLockedForTheCustomer: boolean;
      pmcIsInsideALockedMemberGroup: boolean;
    }
  >;
  // The permit the user is currently viewing/acting on. `selectedPermit` holds
  // the permit order line id (orderLineIdWithVersion, falling back to id);
  // `selectedPermitId` is the permit whose parking space is being edited.
  // Migrated from Apollo reactive vars (CPS-26940).
  selectedPermit: string | undefined;
  selectedPermitId: string | undefined;
  // The pmc the user is buying/unlocking a permit for.
  // Distinct from cityParkingStore.selectedPmc (the city-parking flow's pmc):
  // this is the permit-domain selection, with its own lifecycle. Migrated from
  // the Apollo reactive var.
  selectedPmc: Pmc | undefined;
  // Purchase parameters carried into a permit/prepaid purchase. The start dates
  // drive validFrom; the billing interval drives the chosen frequency. Wrong
  // values here mean the wrong permit is bought — treat as money-adjacent.
  selectedPermitStartDate: Date | undefined;
  selectedPrepaidStartDate: Date | undefined;
  selectedPermitBillingInterval: SelectedPermitBillingInterval | undefined;
  // Permit edit/purchase flow flags. `unsavedEditPermitAction` is the pending
  // save/discard choice while leaving an edited permit; `wasPermitTerminated`
  // flags a just-terminated permit so the UI can react; `blockCompanybenefitInEdit`
  // names the active buy/edit flow that should block company-benefit editing
  // (empty string = not blocked). Migrated from Apollo reactive vars.
  unsavedEditPermitAction: UnsavedEditPermitAction;
  wasPermitTerminated: boolean;
  blockCompanybenefitInEdit: string;
  // When true, Continue on the cancel-permit sheet only confirms a local
  // auto-renewal toggle in Edit and must not TERMINATE/CANCEL the order.
  confirmDisableRenewalOnly: boolean;
  confirmDisableRenewalAction: ConfirmDisableRenewalAction;
  // When true, the terminate-permit sheet uses disable-automatic-renewal copy
  // (Edit toggle and Details for ACTIVE orders). Waiting/CSR keeps cancel copy.
  showDisableRenewalDialog: boolean;
  // Permit redemption: the license plates chosen to redeem/apply to a permit,
  // and the member-group/staff benefit that unlocks the park zone. Migrated
  // from Apollo reactive vars; the server-plate seed helpers in states/common
  // write selectedLicensePlatesPermitRedeem through this store's setter.
  selectedLicensePlatesPermitRedeem: LicensePlate[];
  selectedParkZoneBenefit: ParkZoneBenefit | undefined;
}

interface PermitActions {
  setHsaIdCode: (code: string) => void;
  setCustomerVerificationStatus: (status: VerificationStatus) => void;
  setPmcIsLockedByMemberGroup: (
    pmcId: string,
    pmcIsLockedForTheCustomer: boolean,
    pmcIsInsideALockedMemberGroup: boolean
  ) => void;
  resetPmcIsLockedByMemberGroup: () => void;
  setSelectedPermit: (permit: string | undefined) => void;
  setSelectedPermitId: (permitId: string | undefined) => void;
  setSelectedPmc: (pmc: Pmc | undefined) => void;
  setSelectedPermitStartDate: (date: Date | undefined) => void;
  setSelectedPrepaidStartDate: (date: Date | undefined) => void;
  setSelectedPermitBillingInterval: (
    interval: SelectedPermitBillingInterval | undefined
  ) => void;
  setUnsavedEditPermitAction: (action: UnsavedEditPermitAction) => void;
  setConfirmDisableRenewalOnly: (confirmOnly: boolean) => void;
  setConfirmDisableRenewalAction: (action: ConfirmDisableRenewalAction) => void;
  setShowDisableRenewalDialog: (show: boolean) => void;
  setWasPermitTerminated: (wasTerminated: boolean) => void;
  setBlockCompanybenefitInEdit: (route: string) => void;
  setSelectedLicensePlatesPermitRedeem: (plates: LicensePlate[]) => void;
  setSelectedParkZoneBenefit: (benefit: ParkZoneBenefit | undefined) => void;
}

const initialHsaIdState: HsaIdState = {
  hsaIdCode: '',
  customerVerificationStatus: 'idle',
};

export const usePermitStore = create<PermitState & PermitActions>((set) => ({
  hsaIdState: initialHsaIdState,
  pmcIsLockedByMemberGroup: {},
  selectedPermit: undefined,
  selectedPermitId: undefined,
  selectedPmc: undefined,
  selectedPermitStartDate: undefined,
  selectedPrepaidStartDate: undefined,
  selectedPermitBillingInterval: undefined,
  unsavedEditPermitAction: null,
  confirmDisableRenewalOnly: false,
  confirmDisableRenewalAction: null,
  showDisableRenewalDialog: false,
  wasPermitTerminated: false,
  blockCompanybenefitInEdit: '',
  selectedLicensePlatesPermitRedeem: [],
  selectedParkZoneBenefit: undefined,

  setHsaIdCode: (code) =>
    set((state) => ({
      hsaIdState: { ...state.hsaIdState, hsaIdCode: code },
    })),

  setCustomerVerificationStatus: (status) =>
    set((state) => ({
      hsaIdState: { ...state.hsaIdState, customerVerificationStatus: status },
    })),

  setPmcIsLockedByMemberGroup: (
    pmcId,
    pmcIsLockedForTheCustomer,
    pmcIsInsideALockedMemberGroup
  ) =>
    set((state) => ({
      pmcIsLockedByMemberGroup: {
        ...state.pmcIsLockedByMemberGroup,
        [pmcId]: { pmcIsLockedForTheCustomer, pmcIsInsideALockedMemberGroup },
      },
    })),

  resetPmcIsLockedByMemberGroup: () => set({ pmcIsLockedByMemberGroup: {} }),

  setSelectedPermit: (permit) => set({ selectedPermit: permit }),
  setSelectedPermitId: (permitId) => set({ selectedPermitId: permitId }),
  setSelectedPmc: (pmc) => set({ selectedPmc: pmc }),
  setSelectedPermitStartDate: (date) => set({ selectedPermitStartDate: date }),
  setSelectedPrepaidStartDate: (date) =>
    set({ selectedPrepaidStartDate: date }),
  setSelectedPermitBillingInterval: (interval) =>
    set({ selectedPermitBillingInterval: interval }),
  setUnsavedEditPermitAction: (action) =>
    set({ unsavedEditPermitAction: action }),
  setConfirmDisableRenewalOnly: (confirmOnly) =>
    set({ confirmDisableRenewalOnly: confirmOnly }),
  setConfirmDisableRenewalAction: (action) =>
    set({ confirmDisableRenewalAction: action }),
  setShowDisableRenewalDialog: (show) =>
    set({ showDisableRenewalDialog: show }),
  setWasPermitTerminated: (wasTerminated) =>
    set({ wasPermitTerminated: wasTerminated }),
  setBlockCompanybenefitInEdit: (route) =>
    set({ blockCompanybenefitInEdit: route }),
  setSelectedLicensePlatesPermitRedeem: (plates) =>
    set({ selectedLicensePlatesPermitRedeem: plates }),
  setSelectedParkZoneBenefit: (benefit) =>
    set({ selectedParkZoneBenefit: benefit }),
}));
